import React from "react";

import ListingFieldGroup from "../components/ListingFieldGroup";
import ListingLanguageBlock from "../components/ListingLanguageBlock";
import {
  choiceRow,
  fieldHint,
  fieldInput,
  fieldLabel,
  grid4Wide,
} from "../listingEditorStyles";

/**
 * "Marketing Details": the nine optional presentation fields from the v2
 * listing schema (Migration 118).
 *
 * Three groups now — the letting terms, the district and the flatmates —
 * instead of one flat run of fields. The two bilingual pairs sit in the same
 * language blocks the description and amenity sections use, so DE and EN read
 * as parallel halves rather than as "field DE" and "field EN" side by side.
 *
 * Nothing about the data changed. Every name, every control type, every
 * handler and every coercion is the one that was here before:
 *
 *   - `floor` stays a TEXT input. It holds "EG", "Attika", "2. OG" — turning
 *     it into a number field would silently drop those on the next edit.
 *   - `lease_type` keeps exactly three options. The backend types the field
 *     as Literal["befristet", "unbefristet"], so a fourth value is a 422,
 *     not a convention.
 *   - Empty still means "not maintained" and keeps the block off the public
 *     site; the payload builder is untouched.
 *
 * The labels DO change: the language blocks name their language once, so the
 * fields inside are "Quartier"/"District" rather than "Quartier DE"/"Quartier
 * EN". The smoke test's three affected assertions move with them.
 */
export default function ListingMarketingSection({ form, onChange }) {
  return (
    <>
      <ListingFieldGroup
        title="Vermietungsprofil"
        description="Rahmenbedingungen und zusätzliche Angaben zur Vermietung."
      >
        <div>
          <label className={choiceRow}>
            <input
              type="checkbox"
              name="all_inclusive"
              checked={form.all_inclusive}
              onChange={onChange}
            />
            All-inclusive (Preis-Chip "· all-in")
          </label>
          <p className={fieldHint}>
            Kennzeichnet den Mietpreis auf der Website als All-inclusive.
          </p>
        </div>

        <div className={grid4Wide}>
          <div>
            <label htmlFor="listing-min-stay-months" className={fieldLabel}>
              Mindestmietdauer (Mt.)
            </label>
            <input
              id="listing-min-stay-months"
              type="number"
              name="min_stay_months"
              value={form.min_stay_months}
              onChange={onChange}
              min={0}
              placeholder="z.B. 6"
              className={fieldInput}
            />
          </div>
          <div>
            <label htmlFor="listing-lease-type" className={fieldLabel}>
              Mietverhältnis
            </label>
            <select
              id="listing-lease-type"
              name="lease_type"
              value={form.lease_type}
              onChange={onChange}
              className={fieldInput}
            >
              <option value="">— keine Angabe —</option>
              <option value="befristet">befristet</option>
              <option value="unbefristet">unbefristet</option>
            </select>
          </div>
          <div>
            <label htmlFor="listing-wg-size" className={fieldLabel}>
              WG-Grösse
            </label>
            <input
              id="listing-wg-size"
              type="number"
              name="wg_size"
              value={form.wg_size}
              onChange={onChange}
              min={0}
              placeholder="z.B. 3"
              className={fieldInput}
            />
          </div>
          <div>
            {/* Free text on purpose: "EG", "Attika", "2. OG". */}
            <label htmlFor="listing-floor" className={fieldLabel}>
              Etage
            </label>
            <input
              id="listing-floor"
              type="text"
              name="floor"
              value={form.floor}
              onChange={onChange}
              placeholder="z.B. 2. OG"
              className={fieldInput}
            />
          </div>
        </div>
      </ListingFieldGroup>

      <ListingFieldGroup
        title="Quartier / Bezirk"
        description="Optionale Bezeichnung des Quartiers in Deutsch und Englisch."
      >
        <div className="grid gap-5 lg:grid-cols-2">
          <ListingLanguageBlock title="Deutsch" code="DE">
            <div>
              <label htmlFor="listing-district-de" className={fieldLabel}>
                Quartier
              </label>
              <input
                id="listing-district-de"
                type="text"
                name="district_de"
                value={form.district_de}
                onChange={onChange}
                placeholder="z.B. Seefeld"
                className={fieldInput}
              />
            </div>
          </ListingLanguageBlock>

          <ListingLanguageBlock title="English" code="EN">
            <div>
              <label htmlFor="listing-district-en" className={fieldLabel}>
                District
              </label>
              <input
                id="listing-district-en"
                type="text"
                name="district_en"
                value={form.district_en}
                onChange={onChange}
                placeholder="e.g. Seefeld"
                className={fieldInput}
              />
            </div>
          </ListingLanguageBlock>
        </div>
      </ListingFieldGroup>

      <ListingFieldGroup
        title="Mitbewohner"
        description="Kurze Beschreibung der Mitbewohner bzw. der Wohnsituation."
      >
        <div className="grid gap-5 lg:grid-cols-2">
          <ListingLanguageBlock title="Deutsch" code="DE">
            <div>
              <label htmlFor="listing-flatmates-de" className={fieldLabel}>
                Mitbewohner-Beschreibung
              </label>
              <textarea
                id="listing-flatmates-de"
                name="flatmates_description_de"
                value={form.flatmates_description_de}
                onChange={onChange}
                rows={3}
                placeholder="Das Haus & deine Mitbewohner …"
                className={fieldInput}
              />
            </div>
          </ListingLanguageBlock>

          <ListingLanguageBlock title="English" code="EN">
            <div>
              <label htmlFor="listing-flatmates-en" className={fieldLabel}>
                Flatmates description
              </label>
              <textarea
                id="listing-flatmates-en"
                name="flatmates_description_en"
                value={form.flatmates_description_en}
                onChange={onChange}
                rows={3}
                placeholder="The house & your flatmates …"
                className={fieldInput}
              />
            </div>
          </ListingLanguageBlock>
        </div>
      </ListingFieldGroup>
    </>
  );
}
